import React, { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useCarBrands } from '../../../../../../store/reducers/dictionaries';
import { PURPOSE_SOURCE_VALUES } from '../../../../../../constants/sources';
import { FORM_FIELDS, LABELS } from '../../../../../../constants';
import { Row } from '../../../common';
import { amountTransform, Col, TextInput } from '../../../../../landing.common';
import { AutoBrandSuggest } from './auto.brand';
import { AutoModelSuggest } from './auto.model';
import { useCarModels } from '../../../../../hooks/carModels';
import { anyOptionValue, FormAutoSuggest } from '../../../../ui/suggest/auto';

interface AutoCreditFieldsProps {
    form: UseFormReturn;
}

export const AutoCreditFields: React.FC<AutoCreditFieldsProps> = (props) => {
    const { form } = props;
    const {
        control,
        watch,
        setValue,
        formState: { errors, dirtyFields: _ },
    } = form;

    const [purpose, autoBrand, autoModel, autoBrandId, autoModelId] = watch([
        FORM_FIELDS.PURPOSE,
        FORM_FIELDS.AUTO_BRAND,
        FORM_FIELDS.AUTO_MODEL,
        FORM_FIELDS.AUTO_BRAND_ID,
        FORM_FIELDS.AUTO_MODEL_ID,
    ]);

    const brands = useCarBrands();
    const autoBrandsOptions = brands.map(({ name, id: value }) => ({ name, value: value.toString() }));

    const autoModelIsNeeded = Boolean(autoBrandId);
    const autoYearIsNeeded = Boolean(
        purpose === PURPOSE_SOURCE_VALUES.USED_CAR && autoModel && autoModel !== anyOptionValue,
    );

    const { autoModelsOptions, modelsIsLoading, yearsIsLoading, autoYearsOptions } = useCarModels(
        autoBrandId,
        autoModelId,
    );

    useEffect(() => {
        if (autoBrand && !autoBrandId) {
            const brandId = (autoBrandsOptions.find((item) => item.name === autoBrand) || { value: '' }).value;
            setValue(FORM_FIELDS.AUTO_BRAND_ID, brandId);
        }
        if (autoModel && !autoModelId && autoModelsOptions.length) {
            const modelId = (autoModelsOptions.find((item) => item.name === autoModel) || { value: '' }).value;
            setValue(FORM_FIELDS.AUTO_MODEL_ID, modelId);
        }
    }, [autoBrand, autoModel, autoModelsOptions]);

    return (
        <>
            <Row>
                <Col md-4>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.INITIAL_FEE}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                label={LABELS[FORM_FIELDS.INITIAL_FEE]}
                                value={amountTransform.input(field)}
                                onChange={amountTransform.output(field)}
                                errorMessage={errors[FORM_FIELDS.INITIAL_FEE]?.message}
                            />
                        )}
                    />
                </Col>
                <Col md-4>
                    <AutoBrandSuggest form={form} options={autoBrandsOptions} />
                </Col>
                {autoModelIsNeeded && (
                    <Col md-4>
                        <AutoModelSuggest form={form} options={autoModelsOptions} loading={modelsIsLoading} />
                    </Col>
                )}
            </Row>
            {autoYearIsNeeded && (
                <Row>
                    <Col md-4>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.AUTO_YEAR}
                            render={({ field: { onBlur, ...field } }) => (
                                <FormAutoSuggest
                                    input={{
                                        ...field,
                                        label: 'Год выпуска автомобиля',
                                        errorMessage: errors[FORM_FIELDS.AUTO_YEAR]?.message,
                                    }}
                                    options={autoYearsOptions}
                                    onBlur={onBlur}
                                    onChange={field.onChange}
                                    processing={yearsIsLoading}
                                />
                            )}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};
